<template>
    <div>
        <!-- Main Container -->
        <!-- Shows content always -->
        <v-row>
            <v-col :lg="(currentSelectedTimeEntry || isAddingNewEntry || editingTimeEntryId) ? 3 : 12"
                   :md="(currentSelectedTimeEntry || isAddingNewEntry || editingTimeEntryId) ? 4 : 12"
                   :xl="(currentSelectedTimeEntry || isAddingNewEntry || editingTimeEntryId) ? 2 : 12"
                   class="border-right-divider" cols="12">

                <!-- Left Sidebar: Time Entries List Section -->
                <!-- Displays list of time entries with search functionality -->

                <!-- Search Box -->
                <!-- Only visible when entries exist -->
                <!-- Allows filtering the time entries by name -->
                <v-text-field
                    v-if="hideTimeEntriesListingForMobileIfListingItemSelected() && hasTimeEntries"
                    v-model="timeEntryFilterValue" :label="$gettext('Search time entries')" append-icon="mdi-magnify"
                    class="search-listing-autocomplete" clearable></v-text-field>

                <!-- Time Entries List -->
                <!-- Shows scrollable list of time entries when they exist -->
                <!-- Each item shows rule name and time range -->
                <v-list
                    v-if="currentViewTimeEntriesCount() > 0 && hideTimeEntriesListingForMobileIfListingItemSelected()"
                    class="v-list-wrapper">

                    <v-list-item-group v-model="currentSelectedTimeEntry">

                        <v-list-item v-for="timeEntry in personalControlEntries"
                                     v-show="timeEntrySelectedForMobile() && checkEachTimeEntry(timeEntry.ruleName)"
                                     :key="timeEntry.id"
                                     :class="{ 'v-list-item v-item--active v-list-item--active primary--text': currentSelectedTimeEntry === timeEntry.id }"
                                     :value="timeEntry.id" class="px-0 cursor-pointer" ripple
                                     @click="selectTimeEntry(timeEntry.id)">
                            <v-list-item-content>
                                <v-list-item-title class="text-left">
                                    <v-row>
                                        <v-col class="body-2" cols="12">
                                            <span class="pl-2" v-text="timeEntry.ruleName"></span>
                                        </v-col>
                                    </v-row>
                                </v-list-item-title>
                                <v-list-item-subtitle
                                    class="text-left pl-2 text-truncate caption grey--text font-weight-light">
                                    {{ formatTimeRange(timeEntry.startTime, timeEntry.endTime) }}
                                </v-list-item-subtitle>
                            </v-list-item-content>
                        </v-list-item>
                    </v-list-item-group>
                </v-list>

                <!-- No Results Message -->
                <v-row v-else-if="timeEntryFilterValue.length > 0" class="mx-1 mt-0 mb-3">
                    <v-col>
                        {{
                            $gettextInterpolate($gettext('No matching time entries found for your search: %{ timeEntryFilterValue }'), {timeEntryFilterValue: timeEntryFilterValue})
                        }}
                    </v-col>
                </v-row>

                <!-- No Time Entries Message & creating new entry -->
                <v-row v-else-if="isAddingNewEntry" class="mx-1 mt-0 mb-3">
                    <v-col>
                        {{ $gettext('No time entries configured yet. Portal access is unrestricted.') }}
                    </v-col>
                </v-row>
            </v-col>

            <!-- Right Column: Time Entry Details -->
            <v-col :lg="(currentSelectedTimeEntry || isAddingNewEntry || editingTimeEntryId) ? 9 : 12"
                   :md="(currentSelectedTimeEntry || isAddingNewEntry || editingTimeEntryId) ? 8 : 12"
                   :xl="(currentSelectedTimeEntry || isAddingNewEntry || editingTimeEntryId) ? 10 : 12"
                   class="border-right-divider" cols="12">

                <!-- Personal Controls Content - Will be developed -->
                <v-row class="ma-4">
                    <v-col cols="12">
                        <!--
                        <div class="text-h5 mb-4">{{ $gettext('Personal Controls') }}</div>

                        <p v-if="selectedTimeEntry && !editingTimeEntryId && !isAddingNewEntry" class="body-1 mb-4">
                            {{
                                $gettext('Configure when you are allowed to access this portal. Set specific days and times to control your access.')
                            }}
                        </p>
                        -->

                        <!-- Time Entry Edit/View Mode -->
                        <div v-if="selectedTimeEntry && !editingTimeEntryId">
                            <v-card class="mb-4" outlined>
                                <v-card-text class="text-left">
                                    <div class="d-flex mb-3">
                                        <div class="text-h6 flex-grow-1">{{ selectedTimeEntry.ruleName }}</div>

                                        <v-icon class="primary--text mr-2"
                                                @click="setEditingTimeEntryId(selectedTimeEntry.id)"
                                                v-text="icons.mdiPencilCircleOutline"></v-icon>
                                        <v-icon class="ml-auto primary--text"
                                                @click="deleteTimeEntry(selectedTimeEntry)"
                                                v-text="icons.mdiDeleteCircleOutline"></v-icon>

                                    </div>
                                    <div class="text-body-1 grey--text mb-4">{{ selectedTimeEntry.description }}</div>

                                    <v-row>
                                        <v-col>
                                            {{ formatTimeRange(selectedTimeEntry.startTime, selectedTimeEntry.endTime) }}
                                            <v-chip-group column>
                                                <v-chip v-for="day in selectedTimeEntry.weekDays" :key="day"
                                                        color="primary" outlined small>
                                                    {{ dayNames[day] }}
                                                </v-chip>
                                            </v-chip-group>
                                        </v-col>
                                    </v-row>
                                </v-card-text>
                            </v-card>
                        </div>

                        <!-- New Time Entry Form -->
                        <div v-if="isAddingNewEntry">
                            <v-row class="pl-3 mb-0 pb-0">
                                <v-col class="text-left" cols="12">
                                    <v-btn class="primary--text" small @click="cancelAddNewEntry">
                                        {{ $gettext('Cancel') }}
                                    </v-btn>
                                </v-col>
                                <v-col cols="12">
                                    <v-row>
                                        <v-col cols="12" md="5">
                                            <div class="text-h6 font-weight-regular text-left mt-4 mb-2 d-flex">
                                                <div class="d-flex align-center">
                                                    <v-text-field v-model="newTimeEntry.ruleName"
                                                                  :label="$gettext('Rule Name')"
                                                                  :placeholder="$gettext('Rule Name')"
                                                                  :rules="[rules.required]"
                                                                  class="mt-0 pt-0" outlined required></v-text-field>


                                                </div>
                                            </div>

                                            <div class="text-body-2 mb-3">
                                                <v-textarea v-model="newTimeEntry.description"
                                                            :label="$gettext('Description')"
                                                            :placeholder="$gettext('Description')"
                                                            outlined rows="2"></v-textarea>
                                            </div>

                                            <div class="text-h6 mt-4 mb-4 text-left d-flex">
                                                {{ $gettext('Time Range') }}
                                            </div>

                                            <v-row>
                                                <v-col class="mb-0 pb-0" cols="12" sm="6">
                                                    <v-text-field v-model="newTimeEntry.startTime"
                                                                  :label="$gettext('From')"
                                                                  :placeholder="$gettext('From')"
                                                                  :rules="[rules.required]"
                                                                  :error-messages="getNewStartTimeErrors"
                                                                  outlined type="time"
                                                                  @blur="touchNewStartTime"
                                                                  @input="touchNewStartTime"></v-text-field>
                                                </v-col>
                                                <v-col class="mb-0 pb-0" cols="12" sm="6">
                                                    <v-text-field v-model="newTimeEntry.endTime"
                                                                  :label="$gettext('To')"
                                                                  :placeholder="$gettext('To')"
                                                                  :rules="[rules.required]"
                                                                  :error-messages="getNewEndTimeErrors"
                                                                  outlined type="time"
                                                                  @blur="touchNewEndTime"
                                                                  @input="touchNewEndTime"></v-text-field>
                                                </v-col>
                                            </v-row>

                                            <!-- Days Selection -->
                                            <div class="mb-4">
                                                <div class="subtitle-1 mb-2 text-left">
                                                    {{ $gettext('Please pick the day(s)') }}
                                                </div>
                                                <v-row>
                                                    <v-col cols="12">
                                                        <v-checkbox
                                                            :input-value="newTimeEntry.weekDays && newTimeEntry.weekDays.length === 7"
                                                            :label="$gettext('All Days')" class="mb-2" dense hide-details
                                                            @change="toggleAllDaysForNewEntry"/>
                                                    </v-col>
                                                    <v-col cols="12" md="6">
                                                        <v-checkbox v-model="newTimeEntry.weekDays"
                                                                    :label="$gettext('Monday')"
                                                                    :value="1" class="mb-2" dense hide-details></v-checkbox>
                                                        <v-checkbox v-model="newTimeEntry.weekDays"
                                                                    :label="$gettext('Tuesday')"
                                                                    :value="2" class="mb-2" dense hide-details></v-checkbox>
                                                        <v-checkbox v-model="newTimeEntry.weekDays"
                                                                    :label="$gettext('Wednesday')"
                                                                    :value="3" class="mb-2" dense hide-details></v-checkbox>
                                                        <v-checkbox v-model="newTimeEntry.weekDays"
                                                                    :label="$gettext('Thursday')"
                                                                    :value="4" class="mb-2" dense hide-details></v-checkbox>
                                                    </v-col>
                                                    <v-col cols="12" md="6">
                                                        <v-checkbox v-model="newTimeEntry.weekDays"
                                                                    :label="$gettext('Friday')"
                                                                    :value="5" class="mb-2" dense hide-details></v-checkbox>
                                                        <v-checkbox v-model="newTimeEntry.weekDays"
                                                                    :label="$gettext('Saturday')"
                                                                    :value="6" class="mb-2" dense hide-details></v-checkbox>
                                                        <v-checkbox v-model="newTimeEntry.weekDays"
                                                                    :label="$gettext('Sunday')"
                                                                    :value="7" class="mb-2" dense hide-details></v-checkbox>
                                                    </v-col>
                                                </v-row>
                                            </div>

                                            <!-- Action Buttons -->
                                            <v-row class="mt-4">
                                                <v-col cols="12">
                                                    <v-btn color="primary" @click="saveNewTimeEntry">
                                                        <v-icon left>{{ icons.mdiContentSave }}</v-icon>
                                                        {{ $gettext('Save Entry') }}
                                                    </v-btn>
                                                    <v-btn class="ml-2" outlined @click="cancelAddNewEntry">
                                                        {{ $gettext('Cancel') }}
                                                    </v-btn>
                                                </v-col>
                                            </v-row>
                                        </v-col>
                                    </v-row>
                                </v-col>
                            </v-row>
                        </div>

                        <!-- Time Entry Edit Form (following UsageTimeTab pattern) -->
                        <div v-if="editingTimeEntryId && localTimeEntry">
                            <v-row class="pl-3 mb-0 pb-0">
                                <v-col class="text-left" cols="12">
                                    <v-btn class="primary--text" small @click="cancelEditing">
                                        {{ $gettext('Cancel') }}
                                    </v-btn>
                                </v-col>
                                <v-col cols="12">
                                    <v-row>
                                        <v-col cols="12" md="5">
                                            <div class="text-h6 font-weight-regular text-left mt-4 mb-2 d-flex">
                                                <div class="d-flex align-center">
                                                    <v-text-field v-model="localTimeEntry.ruleName"
                                                                  :label="$gettext('Rule Name')"
                                                                  :placeholder="$gettext('Rule Name')"
                                                                  :rules="[rules.required]"
                                                                  class="mt-0 pt-0" outlined></v-text-field>
                                                </div>
                                            </div>

                                            <div class="text-body-2 mb-3">
                                                <v-textarea v-model="localTimeEntry.description"
                                                            :label="$gettext('Description')"
                                                            :placeholder="$gettext('Description')"
                                                            dense outlined rows="2"></v-textarea>
                                            </div>

                                            <div class="text-h6 mt-4 mb-4 text-left d-flex">
                                                {{ $gettext('Time Range') }}
                                            </div>

                                            <v-row>
                                                <v-col class="mb-0 pb-0" cols="12" sm="6">
                                                    <v-text-field v-model="localTimeEntry.startTime"
                                                                  :label="$gettext('From')"
                                                                  :placeholder="$gettext('From')"
                                                                  :rules="[rules.required]"
                                                                  :error-messages="getStartTimeErrors(localTimeEntry)"
                                                                  outlined type="time"
                                                                  @blur="touchStartTime(localTimeEntry)"
                                                                  @input="touchStartTime(localTimeEntry)"></v-text-field>
                                                </v-col>
                                                <v-col class="mb-0 pb-0" cols="12" sm="6">
                                                    <v-text-field v-model="localTimeEntry.endTime"
                                                                  :label="$gettext('To')"
                                                                  :placeholder="$gettext('To')"
                                                                  :rules="[rules.required]"
                                                                  :error-messages="getEndTimeErrors(localTimeEntry)"
                                                                  outlined type="time"
                                                                  @blur="touchEndTime(localTimeEntry)"
                                                                  @input="touchEndTime(localTimeEntry)"></v-text-field>
                                                </v-col>
                                            </v-row>

                                            <!-- Days Selection -->
                                            <div class="mb-4">
                                                <div class="subtitle-1 mb-2 text-left">
                                                    {{ $gettext('Please pick the day(s)') }}
                                                </div>
                                                <v-row>
                                                    <v-col cols="12">
                                                        <v-checkbox
                                                            :input-value="localTimeEntry.weekDays && localTimeEntry.weekDays.length === 7"
                                                            :label="$gettext('All Days')" class="mb-2" dense hide-details
                                                            @change="toggleAllDays(localTimeEntry)"/>
                                                    </v-col>
                                                    <v-col cols="12" md="6">
                                                        <v-checkbox v-model="localTimeEntry.weekDays"
                                                                    :label="$gettext('Monday')"
                                                                    :value="1" class="mb-2" dense hide-details></v-checkbox>
                                                        <v-checkbox v-model="localTimeEntry.weekDays"
                                                                    :label="$gettext('Tuesday')"
                                                                    :value="2" class="mb-2" dense hide-details></v-checkbox>
                                                        <v-checkbox v-model="localTimeEntry.weekDays"
                                                                    :label="$gettext('Wednesday')"
                                                                    :value="3" class="mb-2" dense hide-details></v-checkbox>
                                                        <v-checkbox v-model="localTimeEntry.weekDays"
                                                                    :label="$gettext('Thursday')"
                                                                    :value="4" class="mb-2" dense hide-details></v-checkbox>
                                                    </v-col>
                                                    <v-col cols="12" md="6">
                                                        <v-checkbox v-model="localTimeEntry.weekDays"
                                                                    :label="$gettext('Friday')"
                                                                    :value="5" class="mb-2" dense hide-details></v-checkbox>
                                                        <v-checkbox v-model="localTimeEntry.weekDays"
                                                                    :label="$gettext('Saturday')"
                                                                    :value="6" class="mb-2" dense hide-details></v-checkbox>
                                                        <v-checkbox v-model="localTimeEntry.weekDays"
                                                                    :label="$gettext('Sunday')"
                                                                    :value="7" class="mb-2" dense hide-details></v-checkbox>
                                                    </v-col>
                                                </v-row>
                                            </div>
                                        </v-col>
                                    </v-row>
                                </v-col>

                                <!-- Save and Cancel Buttons -->
                                <v-col cols="12" class="mt-4">
                                    <v-row>
                                        <v-col cols="12" class="d-flex justify-start">
                                            <v-btn class="mr-2" @click="cancelEditing">
                                                {{ $gettext('Cancel') }}
                                            </v-btn>
                                            <v-btn color="primary" @click="saveEditedTimeEntry">
                                                {{ $gettext('Save') }}
                                            </v-btn>
                                        </v-col>
                                    </v-row>
                                </v-col>
                            </v-row>
                        </div>

                        <!-- No time entry selected -->
                        <!--                        <div v-if="!selectedTimeEntry && !editingTimeEntryId && !isAddingNewEntry && hasTimeEntries"
                                                     class="text-center">
                                                    <v-alert outlined type="info">
                                                        <div class="text-h6 mb-2">{{ $gettext('Select a Time Entry') }}</div>
                                                        <p class="mb-2">{{
                                                                $gettext('Choose a time entry from the list on the left to view its details.')
                                                            }}</p>
                                                        <v-btn color="primary" @click="addNewTimeEntry">
                                                            <v-icon left>{{ icons.mdiPlus }}</v-icon>
                                                            {{ $gettext('Add New Entry') }}
                                                        </v-btn>
                                                    </v-alert>
                                                </div>-->

                        <!-- No time entries at all -->
                        <div v-if="personalControlEntries.length === 0 && !editingTimeEntryId && !isAddingNewEntry"
                             class="text-center">
                            <v-alert color="primary™™™" outlined type="info">
                                <div class="text-h6 mb-2">{{ $gettext('No Portal Access Restrictions') }}</div>
                                <p class="mb-3">{{
                                        $gettext('No portal access times have been configured. Portal access is currently unrestricted.')
                                    }}</p>
                                <v-btn color="primary" @click="addNewTimeEntry">
                                    <v-icon left>{{ icons.mdiPlus }}</v-icon>
                                    {{ $gettext('Add First Entry') }}
                                </v-btn>
                            </v-alert>
                        </div>

                        <!-- Add New Time Entry Button (when not editing) -->
                        <v-btn v-if="!editingTimeEntryId && !isAddingNewEntry && hasTimeEntries" class="mt-6 mb-4 w-100"
                               color="primary"
                               @click="addNewTimeEntry">
                            <v-icon left>{{ icons.mdiPlus }}</v-icon>
                            {{ $gettext('Add Time Entry') }}
                        </v-btn>

                    </v-col>
                </v-row>
            </v-col>
        </v-row>

        <snackbar ref="snackbar"/>
    </div>
</template>

<script>
import {mapState, mapWritableState} from 'pinia'
import {usePersonalControlsStore} from '@/stores/PersonalControlsStore'
import MixinListing from '@/mixins/listing/MixinListing'
import MixinDark from '@/mixins/database/MixinDark'
import MixinIcon from '@/mixins/MixinIcon'
import Snackbar from '@/components/Snackbar.vue'
import MixinSwalConfirmation from '@/mixins/swal/MixinSwalConfirmation'
import MixinPersonalControl from '@/mixins/MixinPersonalControl'

export default {
    name: 'PersonalControlsTab',

    components: {
        Snackbar
    },

    mixins: [
        MixinListing,
        MixinDark,
        MixinIcon,
        MixinSwalConfirmation,
        MixinPersonalControl
    ],

    data() {
        return {
            timeEntryFilterValue: '',
            currentSelectedTimeEntry: null,
            editingTimeEntryId: null,
            isAddingNewEntry: false,
            newTimeEntry: {
                ruleName: '',
                weekDays: [1, 2, 3, 4, 5, 6, 7],
                startTime: '09:00',
                endTime: '17:00',
                description: ''
            },
            localTimeEntry: null,
            startTimeTouched: false,
            endTimeTouched: false,
            newStartTimeTouched: false,
            newEndTimeTouched: false,
            rules: {
                required: value => !!value || this.$gettext('This field is required'),
                endTimeAfterStart: () => {
                    if (this.isAddingNewEntry) {
                        if (!this.newTimeEntry.startTime || !this.newTimeEntry.endTime) return true
                        return this.newTimeEntry.endTime > this.newTimeEntry.startTime || this.$gettext('End time must be after start time')
                    } else {
                        if (!this.localTimeEntry || !this.localTimeEntry.startTime || !this.localTimeEntry.endTime) return true
                        return this.localTimeEntry.endTime > this.localTimeEntry.startTime || this.$gettext('End time must be after start time')
                    }
                }
            },
            dayNames: {
                1: this.$gettext('Monday'),
                2: this.$gettext('Tuesday'),
                3: this.$gettext('Wednesday'),
                4: this.$gettext('Thursday'),
                5: this.$gettext('Friday'),
                6: this.$gettext('Saturday'),
                7: this.$gettext('Sunday')
            }
        }
    },

    computed: {
        ...mapWritableState(usePersonalControlsStore, {
            personalControlsData: 'personalControls'
        }),

        // Get all personal control entries (portal-wide, not device-specific)
        personalControlEntries() {

            // personalControlsData should now be a direct array, not device-specific object
            if (Array.isArray(this.personalControlsData)) {
                return this.personalControlsData;
            } else if (!this.personalControlsData) {
                return [];
            } else {
                return [];
            }
        },

        hasTimeEntries() {
            return this.personalControlEntries.length > 0
        },

        selectedTimeEntry() {
            if (!this.currentSelectedTimeEntry) {
                return null
            }
            return this.personalControlEntries.find(entry => entry.id === this.currentSelectedTimeEntry) || null
        },

        getStartTimeErrors() {
            return (timeEntry) => {
                const errors = [];
                
                if (!this.startTimeTouched) {
                    return errors;
                }
                
                if (timeEntry.startTime && timeEntry.endTime) {
                    const timeDiff = this.calculateTimeDifference(timeEntry.startTime, timeEntry.endTime);
                    
                    if (timeDiff !== null) {
                        if (timeDiff <= 0) {
                            errors.push(this.$gettext('End time must be after start time'));
                        } else if (timeDiff < 60) {
                            errors.push(this.$gettext('Time window must be at least 1 hour'));
                        }
                    }
                }
                
                return errors;
            };
        },

        getEndTimeErrors() {
            return (timeEntry) => {
                const errors = [];
                
                if (!this.endTimeTouched) {
                    return errors;
                }
                
                if (timeEntry.startTime && timeEntry.endTime) {
                    const timeDiff = this.calculateTimeDifference(timeEntry.startTime, timeEntry.endTime);
                    
                    if (timeDiff !== null) {
                        if (timeDiff <= 0) {
                            errors.push(this.$gettext('End time must be after start time'));
                        } else if (timeDiff < 60) {
                            errors.push(this.$gettext('Time window must be at least 1 hour'));
                        }
                    }
                }
                
                return errors;
            };
        },

        getNewStartTimeErrors() {
            const errors = [];
            
            if (!this.newStartTimeTouched) {
                return errors;
            }
            
            if (this.newTimeEntry.startTime && this.newTimeEntry.endTime) {
                const timeDiff = this.calculateTimeDifference(this.newTimeEntry.startTime, this.newTimeEntry.endTime);
                
                if (timeDiff !== null) {
                    if (timeDiff <= 0) {
                        errors.push(this.$gettext('End time must be after start time'));
                    } else if (timeDiff < 60) {
                        errors.push(this.$gettext('Time window must be at least 1 hour'));
                    }
                }
            }
            
            return errors;
        },

        getNewEndTimeErrors() {
            const errors = [];
            
            if (!this.newEndTimeTouched) {
                return errors;
            }
            
            if (this.newTimeEntry.startTime && this.newTimeEntry.endTime) {
                const timeDiff = this.calculateTimeDifference(this.newTimeEntry.startTime, this.newTimeEntry.endTime);
                
                if (timeDiff !== null) {
                    if (timeDiff <= 0) {
                        errors.push(this.$gettext('End time must be after start time'));
                    } else if (timeDiff < 60) {
                        errors.push(this.$gettext('Time window must be at least 1 hour'));
                    }
                }
            }
            
            return errors;
        },
    },

    watch: {
        currentSelectedTimeEntry(newVal) {
            this.editingTimeEntryId = null; // Close edit mode when selection changes
            this.isAddingNewEntry = false; // Close add mode when selection changes
        },
        editingTimeEntryId(newVal, oldVal) {
            if (newVal !== oldVal) {
                this.startTimeTouched = false;
                this.endTimeTouched = false;
            }

            if (newVal && newVal !== oldVal) {
                this.initializeLocalTimeEntry(newVal);
            } else if (!newVal) {
                this.localTimeEntry = null;
            }
        }
    },

    mounted() {
        // If we have time entries but none selected, select the first one
        if (this.hasTimeEntries && !this.currentSelectedTimeEntry) {
            const firstEntry = this.personalControlEntries[0];
            if (firstEntry) {
                this.selectTimeEntry(firstEntry.id);
            }
        }
    },


    methods: {
        selectTimeEntry(entryId) {
            if (entryId) {
                this.currentSelectedTimeEntry = entryId;
                // Prevent toggle behavior
                this.$nextTick(() => {
                    this.currentSelectedTimeEntry = entryId;
                });
            }
        },

        checkEachTimeEntry(ruleName) {
            let entryVisibility = true;
            if (this.timeEntryFilterValue && this.timeEntryFilterValue.length > 0) {
                entryVisibility = ruleName &&
                    ruleName.toLowerCase().includes(this.timeEntryFilterValue.toLowerCase());
            }
            return entryVisibility;
        },

        currentViewTimeEntriesCount() {
            let count = 0
            if (this.personalControlEntries && this.personalControlEntries.length > 0) {
                for (let entry of this.personalControlEntries) {
                    if (this.checkEachTimeEntry(entry.ruleName)) {
                        count++
                    }
                }
            }
            return count
        },

        timeEntrySelectedForMobile() {
            return (
                this.$vuetify.breakpoint.smAndUp ||
                (this.$vuetify.breakpoint.mobile && !this.currentSelectedTimeEntry)
            );
        },

        hideTimeEntriesListingForMobileIfListingItemSelected() {
            return (
                this.$vuetify.breakpoint.smAndUp ||
                (this.$vuetify.breakpoint.mobile && !this.currentSelectedTimeEntry)
            );
        },


        formatTime(time) {
            const [hours, minutes] = time.split(':');
            const hour24 = parseInt(hours);
            const hour12 = hour24 === 0 ? 12 : hour24 > 12 ? hour24 - 12 : hour24;
            const ampm = hour24 >= 12 ? this.$gettext('PM') : this.$gettext('AM');
            return `${hour12}:${minutes} ${ampm}`;
        },

        formatWeekDays(weekDays) {
            return weekDays.map(day => this.dayNames[day]).join(', ');
        },

        formatTimeRange(startTime, endTime) {
            return `${this.formatTime(startTime)} - ${this.formatTime(endTime)}`;
        },

        setEditingTimeEntryId(entryId) {
            if (!this.alertIfEditsNotAllowed()) {
                return;
            }
            this.editingTimeEntryId = entryId;
            this.initializeLocalTimeEntry(entryId);
        },

        initializeLocalTimeEntry(entryId) {
            if (!entryId) {
                this.localTimeEntry = null;
                return;
            }

            if (this.localTimeEntry && this.localTimeEntry.id === entryId) {
                return;
            }

            const entry = this.personalControlEntries.find(e => e.id === entryId);
            if (entry) {
                this.localTimeEntry = JSON.parse(JSON.stringify(entry));
            } else {
                this.localTimeEntry = null;
            }
        },

        getCurrentTimeEntryInEditingState() {
            if (!this.editingTimeEntryId) {
                return [];
            }
            const entry = this.personalControlEntries.find(e => e.id === this.editingTimeEntryId);
            return entry ? [entry] : [];
        },

        toggleAllDays(timeEntry) {
            if (timeEntry.weekDays && timeEntry.weekDays.length === 7) {
                timeEntry.weekDays = [];
            } else {
                timeEntry.weekDays = [1, 2, 3, 4, 5, 6, 7];
            }
        },

        calculateTimeDifference(startTime, endTime) {
            if (!startTime || !endTime) return null;
            
            const [startHours, startMinutes] = startTime.split(':').map(Number);
            const [endHours, endMinutes] = endTime.split(':').map(Number);
            
            if (isNaN(startHours) || isNaN(startMinutes) || isNaN(endHours) || isNaN(endMinutes)) {
                console.error('Invalid time format', startTime, endTime);
                return null;
            }
            
            let startTotalMinutes = startHours * 60 + startMinutes;
            let endTotalMinutes = endHours * 60 + endMinutes;
            
            return endTotalMinutes - startTotalMinutes;
        },

        touchStartTime(timeEntry) {
            this.startTimeTouched = true;
        },

        touchEndTime(timeEntry) {
            this.endTimeTouched = true;
        },

        touchNewStartTime() {
            this.newStartTimeTouched = true;
        },

        touchNewEndTime() {
            this.newEndTimeTouched = true;
        },

        deleteTimeEntry(timeEntry) {
            if (!this.alertIfEditsNotAllowed()) {
                return;
            }
            const swalOptions = {
                title: this.$gettext('Delete Time Entry'),
                description: this.$gettextInterpolate(this.$gettext('Are you sure you want to delete %{ ruleName }?'), {ruleName: timeEntry.ruleName}),
                successCallback: () => this.performDeleteTimeEntry(timeEntry),
                icon: 'warning',
                showCancelButton: true,
                confirmButtonText: this.$gettext('Yes, delete it!'),
                successText: this.$gettext('Time entry has been deleted successfully!')
            };
            this.swalConfirmation(swalOptions);
        },

        performDeleteTimeEntry(timeEntry) {
            const index = this.personalControlEntries.findIndex(e => e.id === timeEntry.id);
            if (index !== -1) {
                this.personalControlEntries.splice(index, 1);
                this.editingTimeEntryId = null;
                this.currentSelectedTimeEntry = null;
                return true;
            }
            return false;
        },

        addNewTimeEntry() {

            let self = this;

            if (!self.alertIfEditsNotAllowed()) {
                return;
            }

            // Reset the new time entry form to default values
            this.newTimeEntry = {
                ruleName: '',
                weekDays: [1, 2, 3, 4, 5, 6, 7],
                startTime: '09:00',
                endTime: '17:00',
                description: ''
            };

            // Reset touch flags
            this.newStartTimeTouched = false;
            this.newEndTimeTouched = false;

            // Show the add new entry form
            this.currentSelectedTimeEntry = null;
            this.editingTimeEntryId = null;

            self.$nextTick(() => {
                self.isAddingNewEntry = true;
            })

        },

        cancelAddNewEntry() {
            this.isAddingNewEntry = false;
            this.newStartTimeTouched = false;
            this.newEndTimeTouched = false;
            // If we have entries, select the first one
            if (this.hasTimeEntries) {
                const firstEntry = this.personalControlEntries[0];
                this.selectTimeEntry(firstEntry.id);
            }
        },

        saveNewTimeEntry() {
            this.newStartTimeTouched = true;
            this.newEndTimeTouched = true;

            const startErrors = this.getNewStartTimeErrors;
            const endErrors = this.getNewEndTimeErrors;

            if (startErrors.length > 0 || endErrors.length > 0) {
                this.$refs.snackbar.showSnackbar(
                    this.$gettext('Please fix validation errors before saving'),
                    'error'
                );
                return;
            }

            // Validate required fields
            if (!this.newTimeEntry.ruleName || !this.newTimeEntry.startTime || !this.newTimeEntry.endTime) {
                this.$refs.snackbar.showSnackbar(this.$gettext('Please fill in all required fields'), 'error');
                return;
            }

            // Validate weekdays selection
            if (!this.newTimeEntry.weekDays || this.newTimeEntry.weekDays.length === 0) {
                this.$refs.snackbar.showSnackbar(this.$gettext('Please select at least one day'), 'error');
                return;
            }

            // Find the next available ID
            const existingIds = this.personalControlEntries.map(e => e.id);
            let nextId = existingIds.length > 0 ? Math.max(...existingIds) + 1 : 1;
            if (nextId < 1) {
                nextId = 1;
            }

            // Create the new time entry
            const newTimeEntry = {
                id: nextId,
                ruleName: this.newTimeEntry.ruleName,
                weekDays: [...this.newTimeEntry.weekDays],
                startTime: this.newTimeEntry.startTime,
                endTime: this.newTimeEntry.endTime,
                description: this.newTimeEntry.description || ''
            };

            // Add to the list
            this.personalControlEntries.push(newTimeEntry);

            // Close the add form and select the new entry
            this.isAddingNewEntry = false;
            this.newStartTimeTouched = false;
            this.newEndTimeTouched = false;
            this.$nextTick(() => {
                this.selectTimeEntry(newTimeEntry.id);
            });

            this.$refs.snackbar.showSnackbar(this.$gettext('Time entry has been created successfully!'), 'success');
        },

        toggleAllDaysForNewEntry() {
            if (this.newTimeEntry.weekDays && this.newTimeEntry.weekDays.length === 7) {
                this.newTimeEntry.weekDays = [];
            } else {
                this.newTimeEntry.weekDays = [1, 2, 3, 4, 5, 6, 7];
            }
        },

        saveEditedTimeEntry() {
            if (!this.localTimeEntry) return;

            this.startTimeTouched = true;
            this.endTimeTouched = true;

            const startErrors = this.getStartTimeErrors(this.localTimeEntry);
            const endErrors = this.getEndTimeErrors(this.localTimeEntry);

            if (startErrors.length > 0 || endErrors.length > 0) {
                this.$refs.snackbar.showSnackbar(
                    this.$gettext('Please fix validation errors before saving'),
                    'error'
                );
                return;
            }

            if (!this.localTimeEntry.ruleName || !this.localTimeEntry.startTime || !this.localTimeEntry.endTime) {
                this.$refs.snackbar.showSnackbar(
                    this.$gettext('Please fill in all required fields'),
                    'error'
                );
                return;
            }

            if (!this.localTimeEntry.weekDays || this.localTimeEntry.weekDays.length === 0) {
                this.$refs.snackbar.showSnackbar(
                    this.$gettext('Please select at least one day'),
                    'error'
                );
                return;
            }

            const index = this.personalControlEntries.findIndex(e => e.id === this.localTimeEntry.id);
            if (index !== -1) {
                const updatedEntry = JSON.parse(JSON.stringify(this.localTimeEntry));
                this.personalControlEntries.splice(index, 1, updatedEntry);
            }

            this.$refs.snackbar.showSnackbar(
                this.$gettext('Time entry has been updated successfully!'),
                'success'
            );

            this.cancelEditing();
        },

        cancelEditing() {
            this.localTimeEntry = null;
            this.editingTimeEntryId = null;
            this.startTimeTouched = false;
            this.endTimeTouched = false;
        }
    }
}
</script>

<style lang="scss" scoped>
.border-right-divider {
    border-right: 1px solid #e0e0e0;
}

/* Custom, iPhone Retina */
@media only screen and (min-width: 320px) {
    .border-right-divider {
        border-right: 1px solid #e0e0e0;
    }
}
</style>